/**
 * Platform-staff (linkco) login action creator (AUTH-03).
 *
 * Posts `{email, password}` (NO tenantSlug) to `/api/v1/_platform/auth/login`.
 * The 200 body is a union: the tokens shape, or a `totp_required` challenge
 * ({ totpSessionToken, … }) with no access token. That challenge is thrown as
 * AUTH_TOTP_REQUIRED carrying `totpSessionToken`, so the login page can move
 * to its code step. JWT `aud` claim is `"platform"`, `tenant_id`
 * is `null` — the platform SPA runs on the root domain, never on a subdomain.
 *
 * Real backend `LoginResponse` is tokens-only — it does NOT include a `user`
 * field. Identity is derived from JWT claims (`claims.sub`) + the email the
 * user typed, with the email local-part as a displayName fallback. MSW
 * legacy fixtures still emit `user` for back-compat, so we treat it as
 * optional and prefer the explicit field when present. Mirrors the
 * defensive pattern in `login.ts:114-123` (tenant variant).
 */
import { commitVerifiedSession } from "./commitVerifiedSession.js";
import { getAcceptLanguageHeader } from "../refresh/authNetworkConfig.js";
import type { AuthSnapshot } from "../types.js";

export interface PlatformLoginArgs {
  email: string;
  password: string;
  apiBaseUrl: string;
}

interface PlatformLoginBody {
  accessToken: string;
  // Honest-nullable: null in cookie-mode where the refresh token rides an
  // HttpOnly cookie instead of the body.
  refreshToken?: string | null;
  expiresIn?: number;
  tokenType?: string;
  // Real backend `LoginResponse` is tokens-only; MSW legacy fixtures include
  // `user` for back-compat. Treat as optional — derive identity from JWT
  // claims + request-time email below when absent.
  user?: { id: string; email: string; displayName: string; roles?: string[] };
}

export async function performPlatformLogin({
  email,
  password,
  apiBaseUrl,
}: PlatformLoginArgs): Promise<{ snapshot: AuthSnapshot }> {
  const res = await fetch(apiBaseUrl + "/api/v1/_platform/auth/login", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...getAcceptLanguageHeader() },
    body: JSON.stringify({ email, password }),
  });
  const body = (await res.json().catch(() => ({}))) as
    | PlatformLoginBody
    | { errorCode?: string; title?: string };

  if (!res.ok) {
    const e = body as { errorCode?: string; title?: string };
    // Mirror login.ts: rate-limited / locked responses must NOT fall through
    // to AUTH_BAD_CREDENTIALS, otherwise the UI shows a misleading
    // "email or password is incorrect" banner.
    const explicitCode = e.errorCode;
    const fallbackCode =
      res.status === 429
        ? "AUTH_RATE_LIMITED"
        : res.status === 423
          ? "ACCOUNT_LOCKED"
          : res.status === 401 || res.status === 400
            ? "AUTH_BAD_CREDENTIALS"
            : "UNKNOWN_ERROR";
    const retryAfterRaw = res.headers.get("retry-after");
    const retryAfterSeconds =
      retryAfterRaw !== null && retryAfterRaw !== "" && /^\d+$/.test(retryAfterRaw)
        ? Number.parseInt(retryAfterRaw, 10)
        : undefined;
    throw Object.assign(new Error(e.title ?? "Platform login failed"), {
      // The RFC 7807 title as a real property — consumers branch on it
      // (message alone is not introspectable as "the server copy").
      title: e.title,
      status: res.status,
      errorCode: explicitCode ?? fallbackCode,
      correlationId: res.headers.get("x-correlation-id"),
      retryAfterSeconds,
      isApiError: true,
    });
  }

  const success = body as PlatformLoginBody;
  // The platform union has NO `type` discriminator: distinguish on
  // fields, and fail closed. A totp_required challenge carries totpSessionToken
  // and no access token; a tokens response carries accessToken.
  const totpSessionToken = (body as { totpSessionToken?: string }).totpSessionToken;
  if (typeof totpSessionToken === "string" && totpSessionToken.length > 0) {
    // Password was right; the account wants its authenticator code next. The
    // login page reads `totpSessionToken` off this error and shows the code
    // step, which completes through `verifyTotp` with audience "platform".
    throw Object.assign(new Error("This account requires an authenticator code."), {
      status: res.status,
      errorCode: "AUTH_TOTP_REQUIRED",
      totpSessionToken,
      correlationId: res.headers.get("x-correlation-id"),
      isApiError: true,
    });
  }
  // Any other 2xx shape is routed through the shared security boundary: it
  // validates the token (decodable, unexpired, aud==="platform", has a subject)
  // BEFORE storing anything and throws AUTH_UNEXPECTED_RESPONSE on a garbage /
  // expired / wrong-audience / token-less body. `hydratePermissions: false`
  // keeps `effectivePermissions: undefined` — platform admin retains residual
  // role-based gating (LINK_STAFF carve-out); aud=platform is the server-side
  // audience boundary, not a permission catalog code. Platform JWT has no
  // tenant claim, so tenantId resolves to null.
  const snapshot = await commitVerifiedSession({
    accessToken: success.accessToken,
    expectedAudience: "platform",
    apiBaseUrl,
    bodyUser: success.user,
    emailHint: email,
    hydratePermissions: false,
    correlationId: res.headers.get("x-correlation-id"),
  });
  return { snapshot };
}
